import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import {
  Box,
  Divider,
  Tooltip,
  Typography,
  useBreakpoint,
} from "@avenue-8/sidekick-ui";
import {
  InsightsSectionConfig,
  InsightsSectionView,
} from "@cma/presentation-generation-logic";
import InfoIcon from "@mui/icons-material/Info";
import { useCallback, useMemo } from "react";
import { InsightItem, InsightsSwiperContainer } from "./Insights.styles";
import { PresentationSectionProps } from "../PresentationSectionProps";
import { useAuth } from "@shared/hooks/useAuth";
import { useGetPropertyTypes } from "@cma/shared/queries/property-search/useGetPropertyTypes";
import { InsightsFilterSheet } from "./components/InsightsFilterSheet";

const FAILURE_MESSAGES = {
  "TERRITORY_NOT_FOUND:NEIGHBORHOOD":
    "No market data found for the provided neighborhood.",
  "TERRITORY_NOT_FOUND:CITY": "No market data found for the provided city.",
  PROPERTY_TYPE_NOT_FOUND:
    "No market data found for the provided property type.",
} as const;

const getFailureMessage = (failure: string) => {
  return (
    FAILURE_MESSAGES[failure as keyof typeof FAILURE_MESSAGES] ??
    `Insights could not be generated. Reason: ${failure}`
  );
};

interface Props
  extends PresentationSectionProps<
    InsightsSectionView,
    InsightsSectionConfig
  > {
  isEditing?: boolean;
  onIsEditingChange?: (isEditing: boolean) => void;
}

export const InsightsEdit = (props: Props) => {
  const {
    sectionView,
    sectionConfig,
    onUpdateSection,
    isEditing = false,
    onIsEditingChange,
  } = props;
  const { id, insights, failure } = sectionView;

  const { user } = useAuth();
  const { data: propertyTypes = [], isLoading: isLoadingPropertyTypes } =
    useGetPropertyTypes({
      mlsSources: user.enabledMlsProviders ?? user.allowedMlsProviders,
    });

  const handleApplyFilters = useCallback(
    (updates: Partial<InsightsSectionConfig>) => {
      onUpdateSection({ sectionConfig: { ...sectionConfig, ...updates } });
    },
    [onUpdateSection, sectionConfig],
  );

  const hasNoDataToDisplay = !insights?.filter((x) => x.mode === "ok")?.length;

  const isLaptopDown = useBreakpoint("laptop");

  const slidesPerView = useMemo(() => {
    if (!insights) return 1;
    if (insights.length === 1) return 1;
    if (isLaptopDown) return 1.3;
    if (insights.length <= 3) return insights.length;
    return 3.3;
  }, [isLaptopDown, insights]);

  const filterSheet = (
    <InsightsFilterSheet
      open={isEditing}
      onClose={() => onIsEditingChange?.(false)}
      sectionConfig={sectionConfig}
      propertyTypes={propertyTypes}
      isLoadingPropertyTypes={isLoadingPropertyTypes}
      onApply={handleApplyFilters}
    />
  );

  if (hasNoDataToDisplay) {
    return (
      <Box id={id} py={3} px={4} textAlign="center">
        <Typography variant="h5" mt={2}>
          Market Insights
        </Typography>
        <Box mt={2} py={6} px={2}>
          <Typography variant="body2" color="textSecondary">
            {failure
              ? getFailureMessage(failure)
              : "Not enough data to generate insights."}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            This section will be hidden.
          </Typography>
        </Box>
        {filterSheet}
      </Box>
    );
  }

  return (
    <Box id={id} pt={4} pb={6} px={4} textAlign="center">
      <Divider sx={{ width: "5rem", margin: "2rem auto 0" }} />
      <Typography variant="h5" mt={2}>
        Market Insights
      </Typography>

      <InsightsSwiperContainer>
        <Swiper
          slidesPerView={slidesPerView}
          modules={[Navigation]}
          allowTouchMove={true}
          simulateTouch={false}
          observer={true}
          observeParents={true}
          resizeObserver={true}
          updateOnWindowResize
          mousewheel
          spaceBetween={insights && insights.length > 1 ? 16 : 0}
        >
          {insights?.map((insight, index) => (
            <SwiperSlide key={`insight-${index}`}>
              <InsightItem aria-disabled={insight.mode === "not-enough-data"}>
                <Typography variant="body1">{insight.content}</Typography>
                <Typography variant="body2">{insight.footer}</Typography>
                {insight.mode === "not-enough-data" && (
                  <Tooltip title="This insight will not be displayed.">
                    <Box>
                      <InfoIcon color="secondary" />
                    </Box>
                  </Tooltip>
                )}
              </InsightItem>
            </SwiperSlide>
          ))}
        </Swiper>
      </InsightsSwiperContainer>

      {filterSheet}
    </Box>
  );
};
